import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import { BsCloudUpload } from "react-icons/bs";
import { FaArrowLeft } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { getallWorkOrders } from "../../../../Redux/actions/Operationsactions/getworkorders";
import { AddWorkOrderApi, UpdateWorkOrderApi } from "../../../../Api/Operations/WorkOrders";
import { getallContractors } from "../../../../Redux/actions/Operationsactions/getcontractors";
import { useSession } from "../../../../context/SessionContext";
import ActionButton from "../../../Common/ActionButton";
import { InputField, SelectField, TextAreaField } from "../../../Common/FormComponents";
import { filterStoreProjects } from "../../projectFilters";
import { formatAmountWithCommas } from "../../../../Utils/utils";
import { getBOQList } from "../../Works/BOQ/boqService";
import "../../AddContractorsAndVendors.css";

const UNITS = ["Sft", "Cft", "Rft", "Kg", "Ton", "Nos", "Bags", "Lump Sum", "Sq. M", "Cu. M"];

const AddWorkOrders = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const contractorsData = useSelector((state) => state.getAllContractors?.data || []);
  const { rolesdata } = useSession();
  const projects = filterStoreProjects(rolesdata?.data?.Projects);
  const populatedata = location.state || {};
  const [isLoading, setIsLoading] = useState(false);
  const [contractors, setContractors] = useState([]);

  const todayStr = new Date().toISOString().split("T")[0];
  const targetDateStr = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  // Form State
  const [formData, setFormData] = useState({
    WorkOrderNo:
      populatedata.WorkOrderNo ||
      populatedata.WONumber ||
      `WO-${new Date().getFullYear()}-0045 (Auto-generated)`,
    ProjectId: populatedata.ProjectId || "",
    ContractorId: populatedata.ContractorId || "",
    WorkArea: populatedata.WorkArea || populatedata.BuildingPhase || "",
    ContractDate: populatedata.ContractDate ? populatedata.ContractDate.split("T")[0] : todayStr,
    TargetCompletionDate: populatedata.TargetCompletionDate
      ? populatedata.TargetCompletionDate.split("T")[0]
      : targetDateStr,
    MaterialClause: populatedata.MaterialClause || "Labour Only (Store Issues)",
    TotalContractValue: populatedata.TotalContractValue,
    RetentionRate: populatedata.RetentionRate !== undefined ? populatedata.RetentionRate : 0,
    TaxRate: populatedata.TaxRate !== undefined ? populatedata.TaxRate : 0,
    MobilizationAdvance:
      populatedata.MobilizationAdvance !== undefined ? populatedata.MobilizationAdvance : 0,
    AuthorizedBy: populatedata.AuthorizedBy || "",
    Description: populatedata.Description || "",
    AttachedFile: populatedata.AttachedFile || "",
  });

  // Schedule of Rates / Items Table
  const [items, setItems] = useState(
    populatedata.Items && populatedata.Items.length > 0 ? populatedata.Items : [],
  );

  // BOQ Import Modal State
  const [showBOQModal, setShowBOQModal] = useState(false);
  const [availableBOQs, setAvailableBOQs] = useState([]);
  const [selectedBOQId, setSelectedBOQId] = useState("");
  const [selectedBOQItemIds, setSelectedBOQItemIds] = useState({});

  useEffect(() => {
    if (location.state && contractors.length > 0) {
      setFormData((prev) => ({
        ...prev,
        ProjectId: location.state.ProjectId || prev.ProjectId,
        ContractorId: location.state.ContractorId || prev.ContractorId,
        TotalContractValue: location.state.TotalContractValue || prev.TotalContractValue,
        RetentionRate:
          location.state.RetentionRate !== undefined
            ? location.state.RetentionRate
            : prev.RetentionRate,
        TaxRate: location.state.TaxRate !== undefined ? location.state.TaxRate : prev.TaxRate,
        Description: location.state.Description || prev.Description,
      }));
    }
  }, [location.state, contractors]);

  useEffect(() => {
    if (!formData.ProjectId) {
      return;
    }
    dispatch(getallContractors(formData.ProjectId));
  }, [dispatch, formData.ProjectId]);

  useEffect(() => {
    if (contractorsData && contractorsData.length > 0) {
      setContractors(contractorsData);
    }
  }, [contractorsData]);

  // Load BOQs when modal opens
  useEffect(() => {
    if (showBOQModal) {
      const boqs = getBOQList();
      setAvailableBOQs(boqs);
      if (boqs.length > 0 && !selectedBOQId) {
        setSelectedBOQId(boqs[0].Id);
      }
    }
  }, [showBOQModal, selectedBOQId]);

  // Recalculate Total Contract Value when items change
  useEffect(() => {
    if (items.length > 0) {
      const sum = items.reduce((acc, curr) => acc + (Number(curr.total) || 0), 0);
      setFormData((prev) => ({
        ...prev,
        TotalContractValue: sum,
      }));
    }
  }, [items]);

  const handleChange = (e) => {
    const { id, name, value } = e.target;
    const fieldName = id || name;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const projectOptions = useMemo(
    () => [
      { value: "", label: "Select Project" },
      ...projects.map((p) => ({ value: p.ProjectId, label: p.ProjectName })),
    ],
    [projects],
  );

  const contractorOptions = useMemo(
    () => [
      { value: "", label: "Select Contractor" },
      ...contractors.map((c) => ({ value: c.Id, label: c.Name })),
    ],
    [contractors],
  );

  const authorizedByOptions = useMemo(
    () => [
      { value: "Project Incharge / PM", label: "Project Incharge / PM" },
      { value: "Project Manager", label: "Project Manager" },
      { value: "Resident Engineer", label: "Resident Engineer" },
      { value: "Chief Auditor", label: "Chief Auditor" },
      { value: "Managing Director", label: "Managing Director" },
    ],
    [],
  );

  // Item Table handlers
  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      const row = { ...copy[index], [field]: value };
      if (field === "qty" || field === "rate") {
        const q = parseFloat(field === "qty" ? value : row.qty) || 0;
        const r = parseFloat(field === "rate" ? value : row.rate) || 0;
        row.total = Math.round(q * r);
      }
      copy[index] = row;
      return copy;
    });
  };

  const handleAddItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      workItem: "",
      source: "Custom Item",
      qty: 1,
      unit: "Sft",
      rate: 0,
      total: 0,
    };
    setItems((prev) => [...prev, newItem]);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      toast.info("At least one line item is required");
      return;
    }
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Import selected BOQ items
  const handleImportBOQItems = () => {
    const currentBOQ = availableBOQs.find((b) => b.Id === selectedBOQId);
    if (!currentBOQ) {
      toast.error("Please select a BOQ");
      return;
    }

    const imported = [];
    (currentBOQ.Sections || []).forEach((sec) => {
      (sec.items || []).forEach((item) => {
        if (selectedBOQItemIds[item.id]) {
          imported.push({
            id: `item-import-${Date.now()}-${item.id}`,
            workItem: `${item.itemCode ? `${item.itemCode} - ` : ""}${item.description}`,
            source: "Imported from BOQ",
            qty: Number(item.qty) || 1,
            unit: item.unit || "Sft",
            rate: Number(item.unitRate) || 0,
            total: Math.round((Number(item.qty) || 1) * (Number(item.unitRate) || 0)),
          });
        }
      });
    });

    if (imported.length === 0) {
      toast.info("Please select at least one item to import");
      return;
    }

    setItems((prev) => [...prev, ...imported]);
    toast.success(`Imported ${imported.length} items from BOQ: ${currentBOQ.BOQNo}`);
    setShowBOQModal(false);
    setSelectedBOQItemIds({});
  };

  async function addWorkOrder() {
    const { ProjectId, ContractorId, TotalContractValue, RetentionRate, TaxRate, Description } =
      formData;

    if (!ProjectId || !ContractorId || !TotalContractValue) {
      toast.info("Please fill all required fields");
      return;
    }

    const workOrderData = {
      WorkOrderId: populatedata?.Id || 0,
      contractorId: ContractorId,
      ProjectId,
      Description: Description || null,
      TotalContractValue: parseFloat(TotalContractValue),
      RetentionRate: parseFloat(RetentionRate) || 0,
      TaxRate: parseFloat(TaxRate) || 0,
      WorkOrderNo: formData.WorkOrderNo,
      WorkArea: formData.WorkArea,
      ContractDate: formData.ContractDate,
      TargetCompletionDate: formData.TargetCompletionDate,
      MaterialClause: formData.MaterialClause,
      AuthorizedBy: formData.AuthorizedBy,
      MobilizationAdvance: parseFloat(formData.MobilizationAdvance) || 0,
      Items: items,
    };

    setIsLoading(true);
    try {
      let result;
      if (populatedata.Id) {
        result = await UpdateWorkOrderApi(workOrderData);
      } else {
        result = await AddWorkOrderApi(workOrderData);
      }

      if (result?.data?.IsSuccess) {
        toast.success(result.data.Message || "Work Order saved successfully");
        dispatch(getallWorkOrders(ProjectId));
        history.goBack();
      } else {
        toast.error(result?.data?.Message || "Failed to save work order");
      }
    } catch (error) {
      toast.error("Error saving work order");
    } finally {
      setIsLoading(false);
    }
  }

  const selectedBOQ = availableBOQs.find((b) => b.Id === selectedBOQId);

  return (
    <div className="pb-5 app_font">
      {/* ─── Standard ERP Page Heading ─── */}
      <div className="col-md-12 text-left page_heading">
        <div className="d-flex align-items-center">
          <FaArrowLeft
            size={16}
            style={{ cursor: "pointer", marginRight: "12px" }}
            title="Back"
            onClick={() => history.goBack()}
          />
          <h4 className="m-0">{populatedata.Id ? "Edit" : "Add"} Work Order</h4>
        </div>
      </div>

      {/* ─── Top Cards (Flex: Left = Header & Contract Details, Right = Scope of Work Table) ─── */}
      <style>
        {`
          .stacked-form-field .input_styling,
          .stacked-form-field .input_address {
            width: 100% !important;
            margin-left: 0 !important;
          }
        `}
      </style>
      <div className="row mt-4">
        {/* Left Card: Header & Contract Details */}
        <div className="col-xl-6 col-12 mb-4">
          <div
            className="p-3 h-100"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center mb-3 pb-2"
              style={{ borderBottom: "2px solid #e7f4f4" }}
            >
              <h6 className="m-0" style={{ fontWeight: 700, color: "#111827" }}>
                Header & Contract Details
              </h6>
            </div>

            <div className="row">
              <InputField
                label="WO Number:"
                name="WorkOrderNo"
                id="WorkOrderNo"
                value={formData.WorkOrderNo}
                disabled
                stacked
                wrapperCol="col-md-6 col-12"
                style={{ backgroundColor: "#f9fafb", color: "#6b7280" }}
              />

              <SelectField
                label="Project / Site:"
                name="ProjectId"
                id="ProjectId"
                value={formData.ProjectId}
                onChange={handleChange}
                options={projectOptions}
                required
                stacked
                wrapperCol="col-md-6 col-12"
              />

              <SelectField
                label="Contractor:"
                name="ContractorId"
                id="ContractorId"
                value={formData.ContractorId}
                onChange={handleChange}
                options={contractorOptions}
                required
                stacked
                wrapperCol="col-md-6 col-12"
              >
                {formData.ContractorId && (
                  <div
                    className="mt-1"
                    style={{
                      fontSize: "11.5px",
                      color: "#0369a1",
                      backgroundColor: "#f0f9ff",
                      padding: "3px 8px",
                      borderRadius: "4px",
                      border: "1px solid #bae6fd",
                      display: "inline-block",
                    }}
                  >
                    ℹ️ Unrecovered Advance: PKR 120,000
                  </div>
                )}
              </SelectField>

              <InputField
                label="Work Area / Phase:"
                name="WorkArea"
                id="WorkArea"
                placeholder="e.g. Tower A - 2nd Floor Slab"
                value={formData.WorkArea}
                onChange={handleChange}
                required
                stacked
                wrapperCol="col-md-6 col-12"
              />

              <InputField
                label="Contract Date:"
                name="ContractDate"
                id="ContractDate"
                type="date"
                value={formData.ContractDate}
                onChange={handleChange}
                required
                stacked
                wrapperCol="col-md-6 col-12"
              />

              <InputField
                label="Target Completion:"
                name="TargetCompletionDate"
                id="TargetCompletionDate"
                type="date"
                value={formData.TargetCompletionDate}
                onChange={handleChange}
                required
                stacked
                wrapperCol="col-md-6 col-12"
              />

              <div className="col-12 stacked-form-field mb-3">
                <label className="input_label d-block m-0 mb-2">Material Clause:</label>
                <div
                  className="d-flex align-items-center flex-wrap"
                  style={{ gap: "24px", minHeight: "36px" }}
                >
                  <label
                    className="d-inline-flex align-items-center m-0 cur_sor"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      name="MaterialClause"
                      value="Labour Only (Store Issues)"
                      checked={formData.MaterialClause === "Labour Only (Store Issues)"}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, MaterialClause: e.target.value }))
                      }
                      style={{
                        accentColor: "#056363",
                        width: "16px",
                        height: "16px",
                        marginRight: "6px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight:
                          formData.MaterialClause === "Labour Only (Store Issues)" ? 600 : 400,
                      }}
                    >
                      Labour Only (Store Issues)
                    </span>
                  </label>

                  <label
                    className="d-inline-flex align-items-center m-0 cur_sor"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="radio"
                      name="MaterialClause"
                      value="With Material (Turnkey)"
                      checked={formData.MaterialClause === "With Material (Turnkey)"}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, MaterialClause: e.target.value }))
                      }
                      style={{
                        accentColor: "#056363",
                        width: "16px",
                        height: "16px",
                        marginRight: "6px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight:
                          formData.MaterialClause === "With Material (Turnkey)" ? 600 : 400,
                      }}
                    >
                      With Material (Turnkey)
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Card: Scope of Work & Schedule of Rates (Table Card) */}
        <div className="col-xl-6 col-12 mb-4">
          <div
            className="p-3 h-100 d-flex flex-column"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center mb-3 pb-2 flex-wrap"
              style={{ borderBottom: "2px solid #e7f4f4", gap: "8px" }}
            >
              <h6 className="m-0" style={{ fontWeight: 700, color: "#111827" }}>
                Scope of Work & Schedule of Rates
              </h6>
              <div style={{ display: "flex", gap: "8px" }}>
                <ActionButton
                  text="Import from BOQ"
                  variant="info"
                  type="button"
                  onClick={() => setShowBOQModal(true)}
                />
                <ActionButton
                  text="+ Add Custom Item"
                  // newBtn="true"
                  type="button"
                  onClick={handleAddItem}
                />
              </div>
            </div>

            <div
              className="table-responsive flex-grow-1"
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                backgroundColor: "#ffffff",
              }}
            >
              <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                <thead style={{ backgroundColor: "#f8fafc", color: "#374151" }}>
                  <tr>
                    <th style={{ width: "40px", textAlign: "center" }}>#</th>
                    <th style={{ minWidth: "220px" }}>Work Item *</th>
                    <th style={{ width: "110px", textAlign: "right" }}>Planned Qty *</th>
                    <th style={{ width: "90px", textAlign: "center" }}>Unit</th>
                    <th style={{ width: "120px", textAlign: "right" }}>Contract Rate*</th>
                    <th style={{ width: "120px", textAlign: "center" }}>Total</th>
                    <th style={{ width: "50px", textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No line items added yet. Click &quot;Import from BOQ&quot; or &quot;+ Add
                        Custom Item&quot;.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td className="text-center text-muted" style={{ verticalAlign: "middle" }}>
                          {idx + 1}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.workItem}
                            placeholder="Enter work item description..."
                            onChange={(e) => handleItemChange(idx, "workItem", e.target.value)}
                            style={{ fontSize: "12.5px" }}
                          />
                          {/* {item.source && (
                            <span
                              className="badge badge-light border text-muted mt-1"
                              style={{ fontSize: "10.5px", fontWeight: 500 }}
                            >
                              {item.source}
                            </span>
                          )} */}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control form-control-sm text-right"
                            value={item.qty}
                            onChange={(e) => handleItemChange(idx, "qty", e.target.value)}
                            style={{ fontSize: "12.5px" }}
                          />
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <select
                            className="form-control form-control-sm"
                            value={item.unit}
                            onChange={(e) => handleItemChange(idx, "unit", e.target.value)}
                            style={{ fontSize: "12px" }}
                          >
                            {UNITS.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control form-control-sm text-right"
                            value={item.rate}
                            onChange={(e) => handleItemChange(idx, "rate", e.target.value)}
                            style={{ fontSize: "12.5px" }}
                          />
                        </td>
                        <td
                          className="text-right font-weight-bold"
                          style={{ verticalAlign: "middle", color: "#056363" }}
                        >
                          Rs. {formatAmountWithCommas(item.total || 0)}
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          <MdDeleteForever
                            title="Delete Item"
                            size="1.3em"
                            className="cur_sor"
                            style={{ cursor: "pointer", color: "#c0392b" }}
                            onClick={() => handleRemoveItem(idx)}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="d-flex align-items-center justify-content-between mt-2">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none font-weight-bold"
                onClick={handleAddItem}
                style={{ color: "#056363", fontSize: "13px" }}
              >
                + Add Item
              </button>
              <div style={{ fontWeight: 600 }}>
                <span>Total Contract Value: </span>
                <span style={{ color: "#056363", fontSize: "14.5px" }}>
                  Rs. {formatAmountWithCommas(formData.TotalContractValue || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── 3. Commercial Terms & Deductions ─────────────────────────────────── */}
      <div className="row">
        {/* Left Card – Special Terms, Notes, Upload, Authorized By */}
        <div className="col-xl-6 col-12">
          <div
            className="p-3 h-100"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center mb-3 pb-2"
              style={{ borderBottom: "2px solid #e7f4f4" }}
            >
              <h6 className="m-0" style={{ fontWeight: 700, color: "#111827" }}>
                Special Terms &amp; Notes
              </h6>
            </div>

            <div className="row">
              <SelectField
                label="Authorized By:"
                name="AuthorizedBy"
                id="AuthorizedBy"
                value={formData.AuthorizedBy}
                onChange={handleChange}
                options={authorizedByOptions}
                required
                stacked
                wrapperCol="col-12"
              />

              <TextAreaField
                label="Notes:"
                name="Description"
                id="Description"
                rows={3}
                placeholder="e.g. Shuttering de-propping only after 14 days curing. Site electricity provided by builder."
                value={formData.Description}
                onChange={handleChange}
                stacked
                wrapperCol="col-md-6 col-12"
              />

              <div className="col-md-6 col-12 stacked-form-field mb-3">
                <label className="input_label d-block m-0 mb-2">Attached Contract:</label>
                <div
                  className="document-upload-box"
                  role="button"
                  tabIndex={0}
                  onClick={() => document.getElementById("workOrderFileInput")?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      document.getElementById("workOrderFileInput")?.click();
                    }
                  }}
                >
                  <BsCloudUpload className="document-upload-icon" />
                  {formData.AttachedFile ? (
                    <div className="document-upload-file">
                      <span>{formData.AttachedFile}</span>
                      <button
                        type="button"
                        className="document-upload-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData((prev) => ({ ...prev, AttachedFile: "" }));
                          const input = document.getElementById("workOrderFileInput");
                          if (input) {
                            input.value = "";
                          }
                        }}
                        title="Remove file"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="document-upload-copy">
                      <strong>Click to upload contract</strong>
                      <span>PDF, image or document</span>
                    </div>
                  )}
                  <input
                    type="file"
                    id="workOrderFileInput"
                    className="d-none"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setFormData((prev) => ({
                          ...prev,
                          AttachedFile: e.target.files[0].name,
                        }));
                        toast.success(`Attached: ${e.target.files[0].name}`);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Card – Commercial Terms & Deductions */}
        <div className="col-xl-6 col-12 ">
          <div
            className="p-3 h-100"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center mb-3 pb-2"
              style={{ borderBottom: "2px solid #e7f4f4" }}
            >
              <h6 className="m-0" style={{ fontWeight: 700, color: "#111827" }}>
                Commercial Terms &amp; Deductions
              </h6>
            </div>

            <div className="row">
              <InputField
                label="Retention Money (%):"
                name="RetentionRate"
                id="RetentionRate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.RetentionRate}
                onChange={handleChange}
                stacked
                wrapperCol="col-md-6 col-12"
              >
                <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                  autocomputed as Rs.{" "}
                  {formatAmountWithCommas(
                    Math.round(
                      ((Number(formData.TotalContractValue) || 0) *
                        (Number(formData.RetentionRate) || 0)) /
                        100,
                    ),
                  )}
                </div>
              </InputField>

              <InputField
                label="WHT / Tax Rate (%):"
                name="TaxRate"
                id="TaxRate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.TaxRate}
                onChange={handleChange}
                stacked
                wrapperCol="col-md-6 col-12"
              >
                <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                  autocomputed as Rs.{" "}
                  {formatAmountWithCommas(
                    Math.round(
                      ((Number(formData.TotalContractValue) || 0) *
                        (Number(formData.TaxRate) || 0)) /
                        100,
                    ),
                  )}
                </div>
              </InputField>

              <InputField
                label="Mobilization Advance:"
                name="MobilizationAdvance"
                id="MobilizationAdvance"
                type="number"
                step="1000"
                min="0"
                value={formData.MobilizationAdvance}
                onChange={handleChange}
                stacked
                wrapperCol="col-md-6 col-12"
              />
            </div>
          </div>
        </div>
      </div>
      {/* ─── Bottom Action Buttons ───────────────────────────────────────────── */}
      <div className="col-12 p-0">
        <div className="float-right mt-4 " style={{ display: "flex", gap: "10px" }}>
          <ActionButton
            text="Cancel"
            variant="cancel"
            type="button"
            onClick={() => history.goBack()}
          />

          <ActionButton
            text="Save"
            icon={BsCloudUpload}
            variant="save"
            onClick={addWorkOrder}
            disabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* ─── Import from BOQ Modal (ERP Standard UI) ─────────────────────────── */}
      {showBOQModal && (
        <div style={{ display: "block", zIndex: 1100 }} className="modalscreen">
          <div className="modal-content-cat_voucher4" style={{ maxWidth: "750px", width: "95%" }}>
            {/* Header */}
            <div className="row">
              <div className="col-12 page_heading">
                <h4 className="text-center pt-2">Import Items from BOQ</h4>
                <span
                  className="close pr-2"
                  onClick={() => {
                    setShowBOQModal(false);
                    setSelectedBOQItemIds({});
                  }}
                >
                  &times;
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="row p-3" style={{ width: "100%" }}>
              <div className="col-12">
                {/* Select BOQ */}
                <div className="row align-items-center mb-3">
                  <div className="col-md-3 text-left p-0">
                    <label className="input_label m-0">Select BOQ:</label>
                  </div>
                  <div className="col-md-9 p-0">
                    <select
                      className="input_styling ml-md-2"
                      style={{ width: "95%" }}
                      value={selectedBOQId}
                      onChange={(e) => {
                        setSelectedBOQId(e.target.value);
                        setSelectedBOQItemIds({});
                      }}
                    >
                      {availableBOQs.map((b) => (
                        <option key={b.Id} value={b.Id}>
                          {b.BOQNo} — {b.BOQName} ({b.Project} / {b.BuildingPhase})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* BOQ Items Listing */}
                {selectedBOQ ? (
                  <div
                    style={{
                      maxHeight: "360px",
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                    }}
                  >
                    <table
                      className="table table-hover table-sm mb-0"
                      style={{ fontSize: "12.5px" }}
                    >
                      <thead style={{ backgroundColor: "#f8fafc", position: "sticky", top: 0 }}>
                        <tr>
                          <th style={{ width: "40px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const newMap = {};
                                if (checked) {
                                  (selectedBOQ.Sections || []).forEach((sec) => {
                                    (sec.items || []).forEach((it) => {
                                      newMap[it.id] = true;
                                    });
                                  });
                                }
                                setSelectedBOQItemIds(newMap);
                              }}
                            />
                          </th>
                          <th>Code</th>
                          <th>Description</th>
                          <th style={{ textAlign: "right" }}>Qty</th>
                          <th style={{ textAlign: "center" }}>Unit</th>
                          <th style={{ textAlign: "right" }}>Rate (PKR)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedBOQ.Sections || []).flatMap((sec) =>
                          (sec.items || []).map((it) => {
                            const isChecked = Boolean(selectedBOQItemIds[it.id]);
                            return (
                              <tr
                                key={it.id}
                                style={{
                                  backgroundColor: isChecked ? "#f0fdfa" : "transparent",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  setSelectedBOQItemIds((prev) => ({
                                    ...prev,
                                    [it.id]: !prev[it.id],
                                  }))
                                }
                              >
                                <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) =>
                                      setSelectedBOQItemIds((prev) => ({
                                        ...prev,
                                        [it.id]: e.target.checked,
                                      }))
                                    }
                                  />
                                </td>
                                <td style={{ fontWeight: 600 }}>{it.itemCode}</td>
                                <td>{it.description}</td>
                                <td className="text-right">{it.qty}</td>
                                <td className="text-center text-muted">{it.unit}</td>
                                <td className="text-right">
                                  {formatAmountWithCommas(it.unitRate)}
                                </td>
                              </tr>
                            );
                          }),
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">No BOQ items available.</div>
                )}

                {/* Footer Buttons */}
                <div className="float-right mt-4">
                  <ActionButton
                    text="Cancel"
                    variant="cancel"
                    type="button"
                    onClick={() => {
                      setShowBOQModal(false);
                      setSelectedBOQItemIds({});
                    }}
                  />
                  <ActionButton
                    text="Import Selected Items"
                    variant="save"
                    type="button"
                    onClick={handleImportBOQItems}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddWorkOrders;
